import React, { useEffect, useRef } from "react"
import { Toolbar, Button, Typography, Box, AppBar } from "@mui/material"
import { useTheme } from "@mui/material/styles"
import { ReactComponent as MenuLogo } from "../assets/svg/menu.svg"
import { ReactComponent as LoginIcon } from "../assets/svg/log-in.svg"
import { ReactComponent as LogoutIcon } from "../assets/svg/log-out.svg"
import { useAuthContext } from "../services/auth"
import { useLocation, useNavigate } from "react-router-dom"
import { SearchBar } from "./SearchBar"
import { BatBox, BatBoxHandle, FillingBox, SnowBox } from "./StyledElements"
import useWindowDimensions from "../services/useWindowDimensions"

interface HeaderBarProps {
  toggleSideMenu: () => void
  noSearchBar?: boolean
  setSearchFocus?: (focused: boolean) => void
}

export const HeaderBar = ({ toggleSideMenu, noSearchBar, setSearchFocus }: HeaderBarProps): React.ReactElement => {
  const { isAuth, logout } = useAuthContext()
  const navigate = useNavigate()
  const { width } = useWindowDimensions()
  const theme = useTheme()
  const location = useLocation()

  const displayClassicButton = theme.name === "classic" && width >= theme.breakpoints.values.sm

  const batBoxRef = useRef<BatBoxHandle>(null)

  const spawnBats = (n: number) => {
    if (!batBoxRef.current || theme.name !== "halloween") return
    for (let i = 0; i < n; i++) {
      batBoxRef.current?.spawn()
    }
  }

  const handleLoginClick = () => {
    if (isAuth()) {
      logout()
    }
    navigate("/login")
  }

  useEffect(() => {
    spawnBats(5)
  }, [theme.name])

  useEffect(() => {
    spawnBats(3)
  }, [location])

  return (
    <AppBar position="sticky" color="transparent">
      <Toolbar
        sx={{
          borderBottom: theme.name === "classic" ? 0 : 1,
          bgcolor: theme.palette.element.header.background,
          alignContent: "center",
          justifyContent: "space-between",
          mx: 0,
        }}
        onMouseEnter={() => spawnBats(1)}
        onMouseLeave={() => spawnBats(1)}
      >
        <Box height={80} width={80}>
          {displayClassicButton || (
            <Button color="inherit" onClick={toggleSideMenu} sx={{ p: "20%", height: "100%", width: "100%" }}>
              <MenuLogo
                style={{
                  stroke: theme.palette.mode === "dark" ? "white" : "black",
                }}
                width="100%"
                height="100%"
              />
            </Button>
          )}
        </Box>
        <Typography
          component="h2"
          variant="h5"
          color="inherit"
          align="center"
          noWrap
          sx={{
            flex: 1,
            zIndex: 5,
          }}
        >
          {noSearchBar ? <></> : <SearchBar setSearchFocus={setSearchFocus} />}
        </Typography>
        <Box height={"80px"} width={"80px"}>
          <Button color="inherit" onClick={handleLoginClick} sx={{ p: "25%", height: "100%", width: "100%" }}>
            {isAuth() ? (
              <LogoutIcon
                style={{
                  stroke: theme.palette.element.header.icon,
                  strokeWidth: 1,
                }}
                width="100%"
                height="100%"
              />
            ) : (
              <LoginIcon
                style={{
                  stroke: theme.palette.element.header.icon,
                  strokeWidth: 1,
                }}
                width="100%"
                height="100%"
              />
            )}
          </Button>
        </Box>
        {theme.name === "christmas" && <SnowBox n={50} baseOpacity={0.7} />}
        {theme.name === "halloween" && <BatBox ref={batBoxRef} defaultFilghtTime={2} />}
        {theme.name === "glass" && (
          <FillingBox
            sx={{
              backdropFilter: theme.name === "glass" ? "blur(5px)" : "unset",
              zIndex: -1,
            }}
          />
        )}
      </Toolbar>
    </AppBar>
  )
}
